import { defineComponent, reactive, ref, SetupContext } from '@vue/composition-api';
import CommonFunction from '@/shared/common-function';
import MessageConst from '@/shared/message-const';
import vueRouter from '@/router/index';                                                     // ルート情報取得
import { OrganizationType, OrgArea, OrgType } from '@/shared/type';                         // 組織情報の型をimport
import SystemConst from '@/shared/system-const';
import HttpStatus from 'http-status-codes';                                                 // HTTPステータスコード取得用
import CommonMessageDialog from '@/components/common/common-message-dialog.component.vue';
import EmailValidator from 'email-validator';                                               // Eメール用Validation

// reactiveDataの型定義
export type reactiveData = {
    windowSize: { x: number; y: number };                               // Windowサイズ
    isValid: boolean;                                                   // 入力チェック状況
    isAddMode: boolean;                                                 // true: 登録処理 false: 更新処理
    organizationId: string;                                             // 組織ID
    displayOrganizationId: string;                                      // 表示用組織ID
    displayDomain: string;                                              // 表示用組織ドメイン名
    organization: OrganizationType;                                     // 組織情報
    locationSelect: OrgArea[];                                          // 地域選択
    orgTypeSelect: OrgType[];                                           // 組織型選択
    searchOrgLocation: string;                                          // 地域検索文字列格納
    searchOrgType: string;                                              // 組織型検索文字列格納
};

export default defineComponent({
    components: { CommonMessageDialog },
    setup(props, ctx: SetupContext) {
        const formHeight = SystemConst.FORM_HEIGHT.HEIGHT;              // フォーム高さ調整用変数
        const router = vueRouter;                                       // ルータ
        const commonRef = ref<any>(null);                               // 共通ダイアログ
        const form = ref<any>(null);                                    // 入力フォーム
        const initialPulldown = { text: '', value: '' };

        const state = reactive<reactiveData>({
            windowSize: { x: 0, y: 0 },                                 // ウィンドウサイズ
            isValid: true,
            isAddMode: true,
            organizationId: '',
            displayOrganizationId: '',
            displayDomain: '',
            organization: {
                affiliationCode: '',                                    // 所属コード
                displayOrder: '',                                       // 表示順
                location: initialPulldown,                              // 地域
                locationName: '',                                       // 地域名称
                organizationType: initialPulldown,                      // 組織型
                organizationTypeName: '',                               // 組織型名称
                organizationName: '',                                   // 組織名称
                organizationNameKana: '',                               // 組織名称（かな）
                domain: '',                                             // 組織ドメイン
                representMail: '',                                      // 代表メールアドレス
                canSalaryGroup: false,                                  // 給与グループ作成
                canPublicRelationsGroup: false,                         // 広報グループ作成
                canOfficialDocumentGroup: false,                        // 公文書グループ作成
                canCms: false,                                          // CMS利用可否
                remarks: '',                                            // 備考
            },
            locationSelect: [],
            orgTypeSelect: [],
            searchOrgLocation: '',
            searchOrgType: '',
        });

        // フォームの各項目のValidationのルール
        // 所属コード
        const affiliationCodeRules = [
            (v: any) =>
                /^[0-9a-zA-Z]*$/.test(v) ||
                CommonFunction.replaceArray(MessageConst.E_REGEX, ['所属コード', '半角英数字']),
            (v: any) => CommonFunction.checkLength(v, maxLength['affiliationCode'], '所属コード')
        ];
        // 表示順
        function displayOrderRules(): string[] {
            const errorMsgs = [];                       // エラーメッセージ格納用配列
            const v = state.organization.displayOrder;  // 入力された表示順
            if (v !== '' && v !== null) {
                // 未入力でなく、nullでない場合
                if (!/^[\d]*$/.test(v)) {
                    errorMsgs.push(CommonFunction.replaceArray(MessageConst.E_REGEX, ['表示順', '半角数字']));
                }
                const resultCheckLength = CommonFunction.checkLength(v, maxLength['displayOrder'], '表示順');
                if (typeof resultCheckLength === 'string') {
                    // エラーメッセージが返ってきた場合
                    errorMsgs.push(resultCheckLength);
                }
            }
            return errorMsgs;
        }
        // 地域
        const locationRules = [
            (v: any) => checkPulldownData(v, '地域')
        ];
        // 組織型
        const organizationTypeRules = [
            (v: any) => checkPulldownData(v, '組織型')
        ];
        // 組織名称
        const organizationNameRules = [
            (v: any) =>
                !!v || CommonFunction.replaceArray(MessageConst.E_REQUIRED, ['組織名称']),
            (v: any) =>
                /^[^/]*$/.test(v) ||
                CommonFunction.replaceArray(MessageConst.E_REGEX, ['組織名称', '「/」以外']),
            (v: any) => CommonFunction.checkLength(v, maxLength['organizationName'], '組織名称')
        ];
        // 組織名称(かな)
        const organizationNameKanaRules = [
            (v: any) =>
                !!v || CommonFunction.replaceArray(MessageConst.E_REQUIRED, ['組織名称（かな）']),
            (v: any) =>
                /^[ぁ-んー]*$/.test(v) ||
                CommonFunction.replaceArray(MessageConst.E_REGEX, ['組織名称（かな）', 'かな']),
            (v: any) => CommonFunction.checkLength(v, maxLength['organizationNameKana'], '組織名称（かな）')
        ];
        // 組織ドメイン
        const domainRulesRules = [
            (v: any) =>
                !!v || CommonFunction.replaceArray(MessageConst.E_REQUIRED, ['組織ドメイン']),
            (v: any) =>
                /^[0-9a-z-.]*$/.test(v) ||
                CommonFunction.replaceArray(MessageConst.E_REGEX, ['組織ドメイン', '小文字の英字、数字、ピリオド（.）、ハイフン（-）のみ']),
            (v: any) => CommonFunction.checkLength(v, maxLength['domain'], '組織ドメイン'),
            (v: any) => checkPeriod(v, 'ピリオド（.）'),
            (v: any) => checkHyphen(v, 'ハイフン（-）'),
        ];
        // 代表メールアドレス
        const representMailRules = [
            (v: any) =>
                !!v || CommonFunction.replaceArray(MessageConst.E_REQUIRED, ['代表メールアドレス']),
            (v: any) =>
                /^[0-9a-z-_'.@]*$/.test(v) ||
                CommonFunction.replaceArray(MessageConst.E_REGEX, ['代表メールアドレス', '小文字の英字、数字、ピリオド（.）、ハイフン（-）、アンダースコア（_）、アポストロフィ（\'）のみ']),
            (v: any) =>
                EmailValidator.validate(v) ||
                CommonFunction.replaceArray(MessageConst.E_EMAIL, ['メールアドレス']),
            (v: any) => CommonFunction.checkLength(v, maxLength['representMail'], '代表メールアドレス'),
        ];
        // 備考
        const remarksRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength['remarks'], '備考')

        ];

        // カウンター表示用最大桁数
        const maxLength = {
            affiliationCode: SystemConst.ORGANIZATION_ADD_MAX_LENGTH.AFFILIATION_CODE,
            displayOrder: SystemConst.ORGANIZATION_ADD_MAX_LENGTH.DISPLAY_ORDER,
            organizationName: SystemConst.ORGANIZATION_ADD_MAX_LENGTH.ORGANIZATION_NAME,
            organizationNameKana: SystemConst.ORGANIZATION_ADD_MAX_LENGTH.ORGANIZATION_NAME_KANA,
            domain: SystemConst.ORGANIZATION_ADD_MAX_LENGTH.DOMAIN,
            representMail: SystemConst.ORGANIZATION_ADD_MAX_LENGTH.REPRESENT_MAIL,
            remarks: SystemConst.ORGANIZATION_ADD_MAX_LENGTH.REMARKS,
        }

        /**
        * 初期表示
        */
        created();
        async function created() {

            // $route.paramsの組織IDをstateのback側に渡すためのorganizationIdと
            // 表示用のdisplayOrganizationIdに設定する
            state.organizationId = ctx.root.$route.params.id;
            state.displayOrganizationId = ctx.root.$route.params.id;

            // organizationIdがnullでない場合
            if (state.organizationId !== undefined) {
                // isAddModeフラグにfalseを設定する
                state.isAddMode = false;

                // titleIcon変更
                ctx.root.$store.commit('setTitleIcon', 'mdi-bank-check');

                // タイトルを「組織編集」に設定
                ctx.root.$store.commit('setMainTitle', '組織編集');

            } else {
                // isAddModeがtrueの場合
                // titleIcon変更
                ctx.root.$store.commit('setTitleIcon', 'mdi-bank-plus');

                // タイトルを「組織登録」に設定
                ctx.root.$store.commit('setMainTitle', '組織登録');
            }

            // 地域リストと組織型リストを取得する処理
            // プルダウン情報取得APIを呼び出す
            // パラメータ:なし
            const response = await ctx.root.$store.dispatch('organization/getPullDowns');

            // HTTPステータスコードが200以外の場合
            if (response.status !== HttpStatus.OK) {
                // 400の場合、入力チェックに関するエラーダイアログを表示する
                // それ以外の場合、エラー画面を表示する
                commonRef.value.errorDisplayTransitionControl(response);

                return;
            }

            // 組織登録画面に取得したプルダウンデータを設定する
            state.locationSelect = response.data.locations;
            state.orgTypeSelect = response.data.organizationTypes;

            // isAddModeがfalseの場合、getOrganization()を呼び組織情報を取得する
            if (!state.isAddMode) {
                getOrganization();
            }
            return true;
        }

        /**
         * 組織情報取得
         */
        async function getOrganization() {

            // 組織情報取得APIを呼び出す
            // パラメータ:組織ID
            const response = await ctx.root.$store.dispatch('organization/getOrganization',
                {
                    organizationId: state.organizationId
                });

            // HTTPステータスコードが200以外の場合
            if (response.status !== HttpStatus.OK) {
                // 404の場合
                if (response.status === HttpStatus.NOT_FOUND) {
                    // 「対象の組織情報は存在しません」という旨のダイアログを表示し、組織一覧画面に遷移する
                    commonRef.value.displayErrorMessageTransitionDialog(response, '/organizations/list');
                }

                // 404以外の場合
                // エラー画面を表示する
                commonRef.value.errorDisplayTransitionControl(response);

                return;
            }

            // HTTPステータスコードが200の場合
            // 取得したデータを入力フォームに表示する
            state.organization = response.data.organization;

            // 地域ID、組織型IDは各valueと一致するtextを表示する
            const locateUpdate = state.locationSelect.find((v) => v.value === response.data.organization['locationId']);
            if (typeof locateUpdate !== 'undefined') {
                // 地域のvalueが一致したものがある場合
                state.organization['location'] = locateUpdate;
            }

            const orgTypeUpdate = state.orgTypeSelect.find((v) => v.value === response.data.organization['organizationTypeId']);
            if (typeof orgTypeUpdate !== 'undefined') {
                // 組織型のvalueが一致したものがある場合
                state.organization['organizationType'] = orgTypeUpdate;
            }

            // 取得した組織ドメイン名を表示用の変数に格納する
            state.displayDomain = state.organization['domain'];
        }

        /**
         * 登録ボタン押下時に「所属コード」、「組織名称」重複チェック
         * 重複がない場合、確認ダイアログを表示する
         */
        async function add() {

            // 入力値をチェックし、エラーならリターンする
            if (!form.value.validate()) {
                const formScroll = document.getElementById('formScroll');
                if (formScroll !== null) {
                    formScroll.scrollTo(0, 0);
                }

                return;
            }

            // 登録情報重複チェックAPIを呼び出す
            // パラメータ:所属コード、組織名称、組織ID(登録処理の場合は空文字)
            const response = await ctx.root.$store.dispatch('organization/checkUnique',
                {
                    affiliationCode: state.organization.affiliationCode,
                    organizationName: state.organization.organizationName,
                    organizationId: state.organizationId
                }
            );

            // HTTPステータスコードが200以外の場合
            if (response.status !== HttpStatus.OK) {
                // 400の場合、入力チェックに関するエラーダイアログを表示する
                // それ以外の場合、エラー画面を表示する
                commonRef.value.errorDisplayTransitionControl(response);

                return;
            }

            // HTTPステータスコードが200の場合
            // 登録処理実行確認ダイアログを表示する
            const displayMessage = CommonFunction.replaceArray(MessageConst.S_CONFIRMATION, ['登録']);

            commonRef.value.displayConfirmationMessageDialog(displayMessage);

        }

        /**
         * 確認ダイアログの「はい」を押下時の処理
         * 入力チェックが全て正常なら登録/更新処理を行い一覧画面に戻る
         */
        async function processingData() {

            // responseの初期化
            let response: any;

            // isAddModeがtrueの場合、組織登録APIを呼び出す
            if (state.isAddMode) {
                // パラメータ:入力フォームの組織情報
                response = await ctx.root.$store.dispatch('organization/addOrganization',
                    {
                        organization: state.organization
                    });
            } else {
                // isAddModeがfalseの場合、組織更新APIを呼び出す
                // パラメータ:入力フォームの組織情報
                response = await ctx.root.$store.dispatch('organization/updateOrganization',
                    {
                        organization: state.organization,
                        organizationId: state.organizationId
                    });
            }

            // HTTPステータスコードが200以外の場合
            if (response.status !== HttpStatus.OK) {
                // 404の場合
                if (response.status === HttpStatus.NOT_FOUND) {
                    // 「対象の組織情報は存在しません」という旨のダイアログを表示し、組織一覧画面に遷移する
                    commonRef.value.displayErrorMessageTransitionDialog(response, '/organizations/list');
                }

                // 400の場合、入力チェックに関するエラーダイアログを表示する
                // 404、400以外の場合、エラー画面を表示する
                commonRef.value.errorDisplayTransitionControl(response);

                return;
            }

            // HTTPステータスコードが200の場合
            // ダイアログを表示し、組織一覧画面に遷移する 
            commonRef.value.displayMessageTransitionDialog(response, '/organizations/list');

            return;
        }

        /**
         * 登録/更新画面にて、キャンセルボタン押下時の処理
         */
        function back() {

            // 組織登録/更新画面を閉じ、組織一覧画面へ戻る
            router.back();
        }

        /**
         * プルダウン情報入力チェック
         *
         * @param selectData    入力されたデータ
         * @param message       表示メッセージ 
         * @returns string      異常値ならエラーメッセージ
         *          boolean     正常値ならtrue
         */
        function checkPulldownData(selectData: any, message: string): string | boolean {

            // クリア、未選択の場合
            if (!selectData || !selectData.text) {
                return CommonFunction.replaceArray(MessageConst.E_UNSELECTED, [message]);
            }

            return true;
        }

        /**
         * ピリオドチェック
         * 
         * @param address       入力されたデータ
         * @param message       表示メッセージ
         * @returns string      異常値ならエラーメッセージ
         *          boolean     正常値ならtrue 
         */
        function checkPeriod(address: string, message: string): string | boolean {

            // 先頭、末尾のピリオドのチェック
            if (/^\./.test(address) || /\.$/.test(address)) {
                return CommonFunction.replaceArray(MessageConst.E_ADDRESS_START_END_ERR, [message]);
            }

            // 連続ピリオドのチェック
            if (/\.\./.test(address)) {
                return CommonFunction.replaceArray(MessageConst.E_ADDRESS_PERIOD_ERR, [message]);
            }

            return true;
        }

        /**
         * ハイフンチェック
         * 
         * @param address       入力されたデータ
         * @param message       表示メッセージ
         * @returns string      異常値ならエラーメッセージ
         *          boolean     正常値ならtrue 
         */
        function checkHyphen(address: string, message: string): string | boolean {

            // 先頭、末尾のハイフンのチェック
            if (/^-/.test(address) || /-$/.test(address)) {
                return CommonFunction.replaceArray(MessageConst.E_ADDRESS_START_END_ERR, [message]);
            }

            return true;
        }

        /**
         * ウィンドウサイズ変更監視
         * 変更されたウィンドウサイズを変数に保持する
         */
        function onResize(): void {
            state.windowSize = { x: window.innerWidth, y: window.innerHeight };
        }

        return {
            state,
            affiliationCodeRules,
            displayOrderRules,
            formHeight,
            locationRules,
            organizationTypeRules,
            organizationNameRules,
            organizationNameKanaRules,
            domainRulesRules,
            representMailRules,
            remarksRules,
            maxLength,
            commonRef,
            form,
            add,
            processingData,
            back,
            onResize
        };
    }
});
